import {
  normalizeImportedAssetFileId,
  type ModelAssetFile,
  type ModelAssetNode,
  type ModelAssetReader,
} from '@puppycad/cad-core'
import { ImportedModelLoadAbortedError } from '@/services/loaders/imported-model-load-guard'
import { mapWithConcurrencyLimit } from '@/services/loaders/map-with-concurrency-limit'
import { logger } from '@/utils'
import {
  buildProjectAssetFileIdentityKey,
} from './project-model-file-identity-utils'

export {
  buildProjectAssetFileIdentityKey,
  isPuppyPackageModelAssetFile,
} from './project-model-file-identity-utils'

/**
 * 项目模型文件读取任务。
 *
 * 设计说明：读取任务需要同时携带 nodeKey 与文件职责，避免不同装配节点使用同名 `_assembly.json`
 * 时在预读缓存中发生误命中。
 */
export interface ProjectNodeFileReadTask {
  /** 当前文件所属的项目模型节点 key。 */
  readonly nodeKey: string
  /** 当前需要通过版本文件接口读取的资产文件。 */
  readonly file: ModelAssetFile
  /** 当前文件在项目模型加载中的职责。 */
  readonly role: 'assembly-manifest' | 'node-package'
}

/**
 * 项目模型文件读取结果。
 */
export interface ProjectNodeFileReadResult extends ProjectNodeFileReadTask {
  /** 已读取到内存中的文件字节。 */
  readonly bytes: Uint8Array
}

/**
 * OpenProject 节点包下载和解析的最大并发数。
 *
 * 设计说明：OpenProject 已经返回完整树，节点包之间没有下载依赖；这里限制并发是为了避免大装配
 * 一次性打爆浏览器连接池，同时保持与 loader 原有读取策略一致。
 */
const MAX_PROJECT_NODE_PACKAGE_LOAD_CONCURRENCY = 6

/**
 * 校验当前模型文件读取尚未被上层请求中止。
 *
 * @param signal 本次模型读取的取消信号。
 */
function assertProjectModelFileReadSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ImportedModelLoadAbortedError()
  }
}

/**
 * 生成项目模型文件读取结果的稳定索引 key。
 *
 * 设计说明：不同装配节点都可能使用 `_assembly.json` 作为 fileId，不能只按文件名缓存；
 * nodeKey 表达 OpenProject 结构位置，role 表达文件职责，二者组合后才能唯一定位一次读取结果。
 *
 * @param nodeKey 文件所属节点 key。
 * @param file 文件描述。
 * @param role 文件职责。
 * @returns 当前加载事务内唯一的读取结果 key。
 */
export function buildProjectNodeFileReadKey(
  nodeKey: string,
  file: ModelAssetFile,
  role: ProjectNodeFileReadTask['role'],
): string {
  return `${nodeKey}::${role}::${file.uuid || file.fileId}::${file.fileId}`
}

/**
 * 按先序遍历收集项目模型节点。
 *
 * 设计说明：容器 entry 补齐需要覆盖整棵项目模型树，而不是只看当前加载计划节点；
 * 这里保留先序顺序，便于后续读取任务仍与 OpenProject 层级顺序一致。
 *
 * @param node 遍历起点节点。
 * @returns 从当前节点开始的先序节点列表。
 */
function collectModelAssetNodesPreorder(node: ModelAssetNode): ModelAssetNode[] {
  return [
    node,
    ...node.children.flatMap((child) => collectModelAssetNodesPreorder(child)),
  ]
}

/**
 * 根据已经下载到内存的版本文件创建 cad-core reader。
 *
 * 设计说明：OpenProject 实际几何加载会先并发下载所有需要的文件，cad-core
 * `executeProjectModelLoad` 只需要复用这些字节解析 `_assembly.json` 和建立加载事务，
 * 不能再次回落到旧下载入口。
 *
 * @param results 当前加载事务已经完成下载的文件结果。
 * @returns 只服务本次加载事务的内存 reader。
 */
export function createPreloadedProjectModelReader(
  results: readonly ProjectNodeFileReadResult[],
): ModelAssetReader {
  const bytesByFileKey = new Map<string, Uint8Array>()

  results.forEach((result) => {
    bytesByFileKey.set(buildProjectAssetFileIdentityKey(result.file), result.bytes)
  })

  return {
    async readAll(file) {
      const bytes = bytesByFileKey.get(buildProjectAssetFileIdentityKey(file))
      if (!bytes) {
        throw new Error(`项目模型预读文件缺失：fileId=${file.fileId}, role=${file.role}`)
      }

      return new Uint8Array(bytes)
    },
    async readRange(file, range) {
      const bytes = bytesByFileKey.get(buildProjectAssetFileIdentityKey(file))
      if (!bytes) {
        throw new Error(`项目模型预读文件缺失：fileId=${file.fileId}, role=${file.role}`)
      }
      if (range.start < 0 || range.end < range.start || range.end >= bytes.byteLength) {
        throw new Error(`项目模型预读 Range 越界：fileId=${file.fileId}, start=${range.start}, end=${range.end}`)
      }

      return new Uint8Array(bytes.slice(range.start, range.end + 1))
    },
  }
}

/**
 * 从项目模型加载计划节点中收集所有需要读取的版本文件。
 *
 * 设计说明：`_assembly.json` 是装配节点元数据，加载优先级不低于几何包；这里统一摊平后交给
 * 授权 reader 并发读取，后续解析阶段再只消费 `node-package`，避免下载阶段遗漏中间装配层。
 *
 * @param nodes 当前加载计划中的节点列表。
 * @returns 按计划顺序摊平后的文件读取任务。
 */
export function collectProjectNodeFileReadTasks(
  nodes: readonly ModelAssetNode[],
): readonly ProjectNodeFileReadTask[] {
  const plannedNodeKeys = new Set(nodes.map((node) => node.nodeKey))
  const tasks = nodes.flatMap((node) => {
    const manifestTasks: ProjectNodeFileReadTask[] = node.assemblyManifest
      ? [{
        nodeKey: node.nodeKey,
        file: node.assemblyManifest,
        role: 'assembly-manifest',
      }]
      : []
    const packageTasks = node.packageFiles.map((packageFile): ProjectNodeFileReadTask => ({
      nodeKey: node.nodeKey,
      file: packageFile,
      role: 'node-package',
    }))
    const embeddedContainerTasks: ProjectNodeFileReadTask[] = (
      node.assetSource?.kind === 'embedded-puppy-occurrence'
      && !plannedNodeKeys.has(node.assetSource.containerNodeKey)
    )
      ? [{
          // Undo/局部恢复只加载 occurrence 子树时，仍以真实容器 owner 作为逻辑 nodeKey，
          // 让组合器可以复用同一套 containerNodeKey + assemblyNodeId 认领协议。
          nodeKey: node.assetSource.containerNodeKey,
          file: node.assetSource.containerFile,
          role: 'node-package',
        }]
      : []

    return [
      ...manifestTasks,
      ...packageTasks,
      ...embeddedContainerTasks,
    ]
  })

  const uniqueTasksByReadKey = new Map<string, ProjectNodeFileReadTask>()
  tasks.forEach((task) => {
    const readKey = buildProjectNodeFileReadKey(task.nodeKey, task.file, task.role)
    if (!uniqueTasksByReadKey.has(readKey)) {
      uniqueTasksByReadKey.set(readKey, task)
    }
  })
  return Array.from(uniqueTasksByReadKey.values())
}

/**
 * 并发读取项目模型文件任务中的版本文件。
 *
 * @param fileReadTasks 已经从加载计划中摊平出的文件读取任务。
 * @param reader 授权版本文件 reader。
 * @param signal 可选取消信号；被取消时统一转换为模型加载取消错误。
 * @returns 已下载到内存的文件字节。
 */
export async function readProjectModelFileReadTasks(
  fileReadTasks: readonly ProjectNodeFileReadTask[],
  reader: ModelAssetReader,
  signal?: AbortSignal,
): Promise<readonly ProjectNodeFileReadResult[]> {
  assertProjectModelFileReadSignalActive(signal)

  try {
    return await mapWithConcurrencyLimit(
      fileReadTasks,
      MAX_PROJECT_NODE_PACKAGE_LOAD_CONCURRENCY,
      async (task): Promise<ProjectNodeFileReadResult> => {
        assertProjectModelFileReadSignalActive(signal)
        return {
          ...task,
          bytes: await reader.readAll(task.file, signal),
        }
      },
    )
  } catch (error) {
    if (signal?.aborted) {
      throw new ImportedModelLoadAbortedError()
    }
    throw error
  }
}

/**
 * 并发读取项目模型加载计划中的版本文件。
 *
 * @param nodes 本次加载计划需要处理的节点列表。
 * @param reader 授权版本文件 reader。
 * @param signal 可选取消信号；用于历史或节点版本快速切换时中止旧请求。
 * @returns 已下载到内存的文件字节。
 */
export async function readProjectModelPlanFiles(
  nodes: readonly ModelAssetNode[],
  reader: ModelAssetReader,
  signal?: AbortSignal,
): Promise<readonly ProjectNodeFileReadResult[]> {
  return readProjectModelFileReadTasks(
    collectProjectNodeFileReadTasks(nodes),
    reader,
    signal,
  )
}

/**
 * 将文件读取结果整理为几何包解析可定位的字节表。
 *
 * @param fileReadResults 已完成下载的版本文件。
 * @returns nodeKey/role/file 组合 key 到字节的映射。
 */
export function buildProjectNodeFileBytesByReadKey(
  fileReadResults: readonly ProjectNodeFileReadResult[],
): Map<string, Uint8Array> {
  const bytesByFileReadKey = new Map<string, Uint8Array>()
  fileReadResults.forEach((result) => {
    bytesByFileReadKey.set(
      buildProjectNodeFileReadKey(result.nodeKey, result.file, result.role),
      result.bytes,
    )
  })

  return bytesByFileReadKey
}

/**
 * 为同一物理文件在不同模型节点下补齐逻辑读取结果。
 *
 * 设计说明：授权下载和内存 reader 可以按文件身份复用字节，但 root `.puppy` direct child 检查必须知道
 * “当前被检查节点自己的 `.puppy`”是否已经 staged。因此同一文件身份被多个 nodeKey 消费时，需要保留
 * 多条 `nodeKey + role + file` 逻辑记录，避免 OpenProject 与 GetCadNodeTree 的补树判断因为 nodeKey
 * 不同而分叉。
 *
 * @param root 当前项目模型加载单元 root。
 * @param fileReadResults 已经 staged 的文件读取结果。
 * @returns 补齐逻辑别名后的读取结果。
 */
function completeLogicalProjectModelFileReadAliases(
  root: ModelAssetNode,
  fileReadResults: readonly ProjectNodeFileReadResult[],
): readonly ProjectNodeFileReadResult[] {
  const results: ProjectNodeFileReadResult[] = [...fileReadResults]
  const readKeys = new Set(
    results.map((result) => buildProjectNodeFileReadKey(result.nodeKey, result.file, result.role)),
  )

  for (const task of collectProjectNodeFileReadTasks(collectModelAssetNodesPreorder(root))) {
    const readKey = buildProjectNodeFileReadKey(task.nodeKey, task.file, task.role)

    if (readKeys.has(readKey)) {
      continue
    }

    const taskIdentityKey = buildProjectAssetFileIdentityKey(task.file)
    const sourceResult = results.find((result) => (
      result.role === task.role
      && buildProjectAssetFileIdentityKey(result.file) === taskIdentityKey
    ))

    if (!sourceResult) {
      continue
    }

    readKeys.add(readKey)
    results.push({
      nodeKey: task.nodeKey,
      file: task.file,
      role: task.role,
      bytes: new Uint8Array(sourceResult.bytes),
    })
  }

  return results.length === fileReadResults.length ? fileReadResults : results
}

/**
 * 判断文件是否可能承载其它项目模型文件 entry。
 *
 * 设计说明：`.assembly.cad.zip` 与部分 `.puppy` 都是 zip 容器。它们在项目模型树中可能只作为
 * 根节点包被下载一次，但树内 direct child 的 `packageFiles` 会指向容器内部的 `parts/...` entry。
 * 删除、Undo 与后续局部切换统一依赖 `ModelAssetFile` 字节缓存，因此这些内部 entry 必须被登记成
 * 一等项目模型文件，而不是在删除链路里特殊解析。
 *
 * @param file 当前已下载的模型资产文件。
 * @returns 该文件是否值得尝试按 zip 容器展开。
 */
function isProjectModelContainerAssetFile(file: ModelAssetFile): boolean {
  const normalizedKind = file.kind.trim().toLowerCase()
  const normalizedFileId = file.fileId.trim().toLowerCase()

  return normalizedKind === '.puppy'
    || normalizedKind === 'application/zip'
    || normalizedFileId.endsWith('.puppy')
    || normalizedFileId.endsWith('.zip')
}

/**
 * 展开项目模型容器文件中的 entry 字节。
 *
 * @param file 当前容器文件描述。
 * @param bytes 当前容器文件字节。
 * @returns 归一化 entry 路径到 entry 字节的映射；不是可展开 zip 时返回空表。
 */
async function unzipProjectModelContainerEntries(
  file: ModelAssetFile,
  bytes: Uint8Array,
): Promise<ReadonlyMap<string, Uint8Array>> {
  if (!isProjectModelContainerAssetFile(file)) {
    return new Map()
  }

  try {
    const { unzipSync } = await import('three/examples/jsm/libs/fflate.module.js')
    const entries = new Map<string, Uint8Array>()

    Object.entries(unzipSync(bytes)).forEach(([entryPath, entryBytes]) => {
      const normalizedPath = normalizeImportedAssetFileId(entryPath)

      if (normalizedPath.length > 0 && !normalizedPath.endsWith('/')) {
        entries.set(normalizedPath, new Uint8Array(entryBytes))
      }
    })

    return entries
  } catch (error) {
    logger.warn('[project-model-file-read-utils] 项目模型容器文件无法展开内部 entry', {
      fileId: file.fileId,
      kind: file.kind,
      error,
    })
    return new Map()
  }
}

/**
 * 用已下载容器内的 entry 补齐当前项目模型树声明的文件读取结果。
 *
 * 设计说明：
 * 1. `.assembly.cad.zip` 初次加载时，子节点 `packageFiles` 指向 `parts/...step.puppy`；
 * 2. 这些 entry 字节已经存在于外层 zip 中，不应等到删除时再走离线包专用分支；
 * 3. 在文件缓存边界补齐后，删除、Undo、Redo、版本切换都继续只消费统一的 `ModelAssetFile` 缓存契约。
 *
 * @param root 当前即将提交的项目模型树。
 * @param stagedFileReadResults 本次已读取到内存、尚未写入缓存的文件结果。
 * @param cachedIdentityKeys loader 内部文件字节缓存当前已有的文件身份 key。
 * @returns 包含容器内部 entry 补齐结果的文件读取结果。
 */
export async function completeProjectModelFileReadResultsFromEmbeddedContainers(
  root: ModelAssetNode,
  stagedFileReadResults: readonly ProjectNodeFileReadResult[],
  cachedIdentityKeys: ReadonlySet<string>,
): Promise<readonly ProjectNodeFileReadResult[]> {
  const logicalFileReadResults = completeLogicalProjectModelFileReadAliases(root, stagedFileReadResults)
  const availableIdentityKeys = new Set(cachedIdentityKeys)

  logicalFileReadResults.forEach((result) => {
    availableIdentityKeys.add(buildProjectAssetFileIdentityKey(result.file))
  })

  const missingTasks = collectProjectNodeFileReadTasks(collectModelAssetNodesPreorder(root))
    .filter((task) => !availableIdentityKeys.has(buildProjectAssetFileIdentityKey(task.file)))

  if (missingTasks.length === 0) {
    return logicalFileReadResults
  }

  const missingTasksByFileId = new Map<string, ProjectNodeFileReadTask[]>()

  missingTasks.forEach((task) => {
    const normalizedFileId = normalizeImportedAssetFileId(task.file.fileId)
    const current = missingTasksByFileId.get(normalizedFileId) ?? []

    missingTasksByFileId.set(normalizedFileId, [...current, task])
  })

  const completedResults: ProjectNodeFileReadResult[] = []
  const completedIdentityKeys = new Set(availableIdentityKeys)

  for (const result of logicalFileReadResults) {
    const entries = await unzipProjectModelContainerEntries(result.file, result.bytes)

    if (entries.size === 0) {
      continue
    }

    for (const [entryPath, entryBytes] of entries) {
      const matchedTasks = missingTasksByFileId.get(entryPath)

      if (!matchedTasks) {
        continue
      }

      for (const task of matchedTasks) {
        const identityKey = buildProjectAssetFileIdentityKey(task.file)

        if (completedIdentityKeys.has(identityKey)) {
          continue
        }

        completedIdentityKeys.add(identityKey)
        completedResults.push({
          nodeKey: task.nodeKey,
          file: task.file,
          role: task.role,
          bytes: new Uint8Array(entryBytes),
        })
      }
    }
  }

  if (completedResults.length === 0) {
    return logicalFileReadResults
  }

  return completeLogicalProjectModelFileReadAliases(
    root,
    [...logicalFileReadResults, ...completedResults],
  )
}

/**
 * 收集组合整树前仍然缺失的版本文件读取任务。
 *
 * 设计说明：局部切换只应主动下载目标子树，未变化兄弟节点继续复用字节缓存；但缓存会在成功切换后裁剪，
 * 也可能因为旧加载路径没有覆盖到兄弟节点而处于冷缓存状态。组合器当前需要重新生成整棵 Object3D，
 * 因此这里在不扩大常驻缓存范围的前提下，仅按需补读当前整树实际缺失的文件。
 *
 * @param nodes 本次整树组合需要消费的节点集合。
 * @param stagedFileReadResults 本次局部计划已经读到、尚未写入缓存的文件。
 * @param cachedIdentityKeys loader 内部文件字节缓存当前已有的文件身份 key。
 * @returns 缓存和本次已读结果都无法满足的最小读取任务集合。
 */
export function collectMissingProjectModelFileReadTasksFromCache(
  nodes: readonly ModelAssetNode[],
  stagedFileReadResults: readonly ProjectNodeFileReadResult[],
  cachedIdentityKeys: ReadonlySet<string>,
): readonly ProjectNodeFileReadTask[] {
  const availableIdentityKeys = new Set(cachedIdentityKeys)
  for (const result of stagedFileReadResults) {
    availableIdentityKeys.add(buildProjectAssetFileIdentityKey(result.file))
  }

  const missingTasks: ProjectNodeFileReadTask[] = []
  const scheduledIdentityKeys = new Set<string>()

  for (const task of collectProjectNodeFileReadTasks(nodes)) {
    const identityKey = buildProjectAssetFileIdentityKey(task.file)
    if (availableIdentityKeys.has(identityKey) || scheduledIdentityKeys.has(identityKey)) {
      continue
    }
    scheduledIdentityKeys.add(identityKey)
    missingTasks.push(task)
  }

  return missingTasks
}
